
import React, { useState } from 'react';
import { Users, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface CompanyUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  companyRole?: string;
}

interface UserLeadFilterProps {
  companyUsers: CompanyUser[];
  selectedUserId: string | null;
  onUserSelect: (userId: string | null) => void;
  isLoading: boolean;
}

const UserLeadFilter: React.FC<UserLeadFilterProps> = ({
  companyUsers,
  selectedUserId,
  onUserSelect,
  isLoading,
}) => {
  const getSelectedLabel = () => {
    if (!selectedUserId) return 'All Company Leads';
    if (selectedUserId === 'my-leads') return 'My Leads';
    
    const user = companyUsers.find(u => u.id === selectedUserId);
    return user ? `${user.first_name} ${user.last_name}'s Leads` : 'Selected User';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1" disabled={isLoading}>
          <Users className="h-4 w-4 mr-1" />
          {getSelectedLabel()}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel>Filter by user</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={selectedUserId || ''} onValueChange={(value) => onUserSelect(value === '' ? null : value)}>
          <DropdownMenuRadioItem value="my-leads">
            <User className="h-4 w-4 mr-2" />
            <span>My Leads</span>
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="">
            <Users className="h-4 w-4 mr-2" />
            <span>All Company Leads</span>
          </DropdownMenuRadioItem>
          
          {companyUsers.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Team Members</DropdownMenuLabel>
              
              {companyUsers.map(user => (
                <DropdownMenuRadioItem key={user.id} value={user.id}>
                  <div className="flex items-center">
                    <Avatar className="h-6 w-6 mr-2">
                      <AvatarImage src={user.avatar_url || ''} />
                      <AvatarFallback>
                        {user.first_name && user.last_name
                          ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
                          : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span>
                      {user.first_name} {user.last_name}
                    </span>
                  </div>
                </DropdownMenuRadioItem>
              ))}
            </>
          )}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserLeadFilter;
